// src/components/IdeaList.js
import React, { useEffect, useState } from 'react';
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import { ArrowUpDown, Loader2, Plus, SlidersHorizontal, ChevronDown } from 'lucide-react';
import IdeaCard from './IdeaCard';
import IdeaDetailModal from './IdeaDetailModal';
import IdeaCreateModal from './IdeaCreateModal';
import IdeaEnhancementModal from './IdeaEnhancementModal';
import QuickIdeaInput from './QuickIdeaInput';
import PathSelectorModal from './PathSelectorModal';
import GuidedJourneyModal from './GuidedJourneyModal';
import EmptyState from './EmptyState';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../firebase';
import { subscribeToIdeas, subscribeToTags, createIdea, updateIdea, deleteIdea } from '../services/ideaService';
import { collection, getDocs, query, where, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { decryptIfNeeded } from '../utils/encryption';
import {
  enhanceIdea,
  consumeFreePassAutopilot,
  ENHANCEMENT_DENIAL_REFRESH_CODES,
} from '../utils/enhanceIdea';
import { useEntitlementSummary } from '../context/EntitlementSummaryContext';
import {
  evaluateEnhancementEntitlementHint,
  getEntitlementFooterHint,
  shouldShowSubscribeCta,
  shouldShowManageBillingCta,
} from '../utils/enhancementEntitlementHint';
import { createCheckoutSession, createBillingPortalSession } from '../utils/billingCheckout';
import {
  sendFirebaseVerificationEmail,
  reloadCurrentAuthUser,
} from '../utils/emailVerification';

/** US-03: idempotency key for POST /api/consume-free-pass */
function newFreePassIdempotencyKey() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `k-${Date.now()}-${Math.random().toString(36).slice(2, 14)}`;
}
// Add status order constant at the top of the file
const STATUS_ORDER = {
  'Drafted': 1,
  'In Progress': 2,
  'Completed': 3,
  'Paused': 4,
  'Archived': 5
};

function IdeaList({ setHasUnsavedChanges }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, loading, authError] = useAuthState(auth);
  const [ideas, setIdeas] = useState([]);
  const [tags, setTags] = useState({});
  const [statuses, setStatuses] = useState([]);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [selectedTags, setSelectedTags] = useState(() => {
    const savedTags = localStorage.getItem('selectedTags');
    return savedTags ? JSON.parse(savedTags) : [];
  });
  const [selectedStatuses, setSelectedStatuses] = useState(() => {
    const savedStatuses = localStorage.getItem('selectedStatuses');
    return savedStatuses ? JSON.parse(savedStatuses) : [];
  });
  const [selectedIdeaId, setSelectedIdeaId] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [searchParams] = useSearchParams();
  const urlTag = searchParams.get('tag');
  const [sortBy, setSortBy] = useState('rating');
  const [sortDirection, setSortDirection] = useState('desc');
  const [availableTags, setAvailableTags] = useState([]);
  
  // New state for three-path flow
  const { summary: entitlementSummary, loading: entitlementLoading, error: entitlementError } =
    useEntitlementSummary();
  const enhancementEntitlement = evaluateEnhancementEntitlementHint({
    summary: entitlementSummary,
  });
  /** US-11: server only requires verification for complimentary free_pass, not paid/override */
  const needsEmailVerificationForComplimentaryAi =
    Boolean(user?.email) &&
    user?.emailVerified !== true &&
    enhancementEntitlement.reason === 'free_pass';
  const pathSelectorFooterHint = needsEmailVerificationForComplimentaryAi
    ? 'Verify your email to use your complimentary AI pass (check your inbox or tap Resend verification in the path selector).'
    : getEntitlementFooterHint({
        loading: entitlementLoading,
        summary: entitlementSummary,
        allowed: enhancementEntitlement.allowed,
        snapshotError: entitlementError,
      });
  const [verificationNotice, setVerificationNotice] = useState(null);
  const [verificationResendBusy, setVerificationResendBusy] = useState(false);
  const [verificationReloadBusy, setVerificationReloadBusy] = useState(false);
  const [billingActionBusy, setBillingActionBusy] = useState(false);
  const [showSignupVerifyBanner, setShowSignupVerifyBanner] = useState(false);
  const [pendingDescription, setPendingDescription] = useState('');
  const [isPathSelectorOpen, setIsPathSelectorOpen] = useState(false);
  const [isGuidedJourneyOpen, setIsGuidedJourneyOpen] = useState(false);
  const [isEnhancementModalOpen, setIsEnhancementModalOpen] = useState(false);
  const [enhancement, setEnhancement] = useState(null);
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);

  useEffect(() => {
    localStorage.setItem('selectedTags', JSON.stringify(selectedTags));
  }, [selectedTags]);

  useEffect(() => {
    localStorage.setItem('selectedStatuses', JSON.stringify(selectedStatuses));
  }, [selectedStatuses]);

  useEffect(() => {
    if (location.state?.emailVerificationSent) {
      setShowSignupVerifyBanner(true);
      navigate('.', { replace: true, state: {} });
    }
  }, [location.state?.emailVerificationSent, navigate]);

  useEffect(() => {
    if (!user) return;

    setFetchLoading(true);

    // Subscribe to real-time ideas updates
    const unsubscribeIdeas = subscribeToIdeas(
      user.uid,
      (ideasData) => {
        setIdeas(ideasData);
        setFetchLoading(false);
      },
      (error) => {
        console.error('Error in ideas subscription:', error);
        setFetchLoading(false);
      }
    );

    // Subscribe to real-time tags updates
    const unsubscribeTags = subscribeToTags(
      user.uid,
      (tagsData) => {
        setTags(tagsData);
      },
      (error) => {
        console.error('Error in tags subscription:', error);
      }
    );

    // Cleanup subscriptions on unmount or when user changes
    return () => {
      unsubscribeIdeas();
      unsubscribeTags();
    };
  }, [user, urlTag]);

  useEffect(() => {
    const uniqueStatuses = [...new Set(ideas.map(idea => idea.status))]
      .filter(Boolean)
      .sort((a, b) => (STATUS_ORDER[a] || 999) - (STATUS_ORDER[b] || 999));
    
    setStatuses(uniqueStatuses);
  }, [ideas]);

  useEffect(() => {
    const fetchTags = async () => {
      if (!user) return;
      try {
        const tagsRef = collection(db, 'tags');
        const q = query(tagsRef, where('userId', '==', user.uid));
        const querySnapshot = await getDocs(q);
        const encryptionKey = process.env.REACT_APP_ENCRYPTION_KEY;
        const tagsList = querySnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            // Decrypt tag name (tags are encrypted in Firestore)
            name: decryptIfNeeded(data.name, encryptionKey)
          };
        });
        setAvailableTags(tagsList);
      } catch (error) {
        console.error('Error fetching tags:', error);
      }
    };

    fetchTags();
  }, [user]);

  const handleResendVerificationEmail = async () => {
    setVerificationNotice(null);
    setVerificationResendBusy(true);
    try {
      const r = await sendFirebaseVerificationEmail();
      if (r === 'sent') {
        setVerificationNotice('Verification email sent. Check your inbox.');
      } else if (r === 'already_verified') {
        setVerificationNotice('Your email is already verified. Tap refresh below.');
      } else if (r === 'no_email') {
        setVerificationNotice('No email address on this account.');
      }
    } catch (e) {
      setVerificationNotice(e?.message || 'Could not send verification email.');
    } finally {
      setVerificationResendBusy(false);
    }
  };

  const handleRefreshAuthAfterVerification = async () => {
    setVerificationNotice(null);
    setVerificationReloadBusy(true);
    try {
      await reloadCurrentAuthUser();
      setVerificationNotice('Account refreshed. If you just verified, try Autopilot or Guided again.');
    } catch (e) {
      setVerificationNotice(e?.message || 'Could not refresh your session.');
    } finally {
      setVerificationReloadBusy(false);
    }
  };

  const handleSubscribeCheckout = async () => {
    setBillingActionBusy(true);
    try {
      const url = await createCheckoutSession();
      window.location.href = url;
    } catch (e) {
      alert(e?.message || 'Could not start checkout.');
      setBillingActionBusy(false);
    }
  };

  const handleManageBilling = async () => {
    setBillingActionBusy(true);
    try {
      const url = await createBillingPortalSession();
      window.location.href = url;
    } catch (e) {
      alert(e?.message || 'Could not open billing portal.');
      setBillingActionBusy(false);
    }
  };

  const showSubscribeCta =
    !entitlementLoading && shouldShowSubscribeCta(entitlementSummary) && !enhancementEntitlement.allowed;
  const showManageBillingCta =
    !entitlementLoading && shouldShowManageBillingCta(entitlementSummary);

  const handleCardClick = (ideaId) => {
    setSelectedIdeaId(ideaId);
    setIsDetailModalOpen(true);
  };

  const handleModalClose = () => {
    setIsDetailModalOpen(false);
    setSelectedIdeaId(null);
  };

  const handleCreateModalOpen = () => {
    setIsCreateModalOpen(true);
  };

  const handleCreateModalClose = () => {
    setIsCreateModalOpen(false);
  };

  const handleSaveInModal = async (id, updatedData) => {
    try {
      setFetchLoading(true);
      
      if (!user || !id) {
        throw new Error('Missing user or idea ID');
      }
  
      // Use service layer to update idea
      await updateIdea(user.uid, id, updatedData);
      
      // Note: Local state will be updated automatically via the real-time subscription
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error('Error saving idea:', error);
      alert('Failed to save changes. Please try again.');
      throw error;
    } finally {
      setFetchLoading(false);
    }
  };

  const handleDeleteInModal = async (id) => {
    if (!user) return;
    
    try {
      // Use service layer to delete idea
      await deleteIdea(user.uid, id);
      // Note: Local state will be updated automatically via the real-time subscription
    } catch (err) {
      console.error('Error deleting idea:', err);
      alert('Failed to delete idea. Please try again.');
    }
  };

  const handleCreateIdea = async (formData) => {
    if (!user) {
      alert('Please sign in to create an idea');
      return;
    }

    try {
      setFetchLoading(true);
      
      // Use service layer to create idea
      await createIdea(user.uid, formData);
      
      // Note: Local state will be updated automatically via the real-time subscription
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error('Error creating idea:', error);
      alert('Failed to create idea. Please try again.');
    } finally {
      setFetchLoading(false);
    }
  };

  // --- New Three-Path Flow Handlers ---
  
  // Handle quick idea submission
  const handleQuickIdeaSubmit = (description) => {
    setPendingDescription(description);
    setIsPathSelectorOpen(true);
  };

  // Handle path selection
  const handlePathSelect = async (path) => {
    setIsPathSelectorOpen(false);
    
    switch (path) {
      case 'raw':
        await handleRawSave(pendingDescription);
        break;
      case 'autopilot':
        await handleAutopilot(pendingDescription);
        break;
      case 'guided':
        setIsGuidedJourneyOpen(true);
        break;
      default:
        console.error('Unknown path:', path);
    }
  };

  // Raw Save path
  const handleRawSave = async (description) => {
    if (!user) return;
    
    try {
      setFetchLoading(true);
      const title = description.trim().substring(0, 50) + (description.length > 50 ? '...' : '');
      
      await createIdea(user.uid, {
        idea: title,
        description: description,
        tags: [],
        status: 'Drafted',
        rating: 0,
        subtasks: [],
        lessonsLearned: '',
        resources: [],
        color: null,
        developmentStatus: 'raw',
        developmentPath: null,
        lastDevelopedAt: null
      });
      
      setPendingDescription('');
    } catch (error) {
      console.error('Error saving raw idea:', error);
      alert('Failed to save idea. Please try again.');
    } finally {
      setFetchLoading(false);
    }
  };

  // Autopilot path
  const handleAutopilot = async (description) => {
    if (!user) return;
    
    try {
      setIsEnhancing(true);
      const result = await enhanceIdea(description);
      setEnhancement(result);
      setIsEnhancementModalOpen(true);
    } catch (error) {
      console.error('Error enhancing idea:', error);
      if (error?.code && ENHANCEMENT_DENIAL_REFRESH_CODES.has(error.code) && auth.currentUser) {
        try {
          await auth.currentUser.getIdToken(true);
        } catch (_) {
          /* ignore */
        }
      }
      alert(error.message || 'Failed to enhance idea. Please try again.');
    } finally {
      setIsEnhancing(false);
    }
  };

  // Handle regeneration in enhancement modal
  const handleRegenerate = async () => {
    if (!pendingDescription) return;
    
    try {
      setIsRegenerating(true);
      const result = await enhanceIdea(pendingDescription);
      setEnhancement(result);
    } catch (error) {
      console.error('Error regenerating:', error);
      if (error?.code && ENHANCEMENT_DENIAL_REFRESH_CODES.has(error.code) && auth.currentUser) {
        try {
          await auth.currentUser.getIdToken(true);
        } catch (_) {
          /* ignore */
        }
      }
      alert(error.message || 'Failed to regenerate. Please try again.');
    } finally {
      setIsRegenerating(false);
    }
  };

  // Handle applying enhancement (Autopilot completion)
  const handleApplyEnhancement = async (enhancedData) => {
    if (!user) return;
    
    try {
      setFetchLoading(true);
      
      // Format plan steps as markdown and append to description
      let fullDescription = enhancedData.description || '';
      if (enhancedData.planSteps && Object.keys(enhancedData.planSteps).length > 0) {
        fullDescription += '\n\n## Plan Steps\n\n';
        for (const [category, steps] of Object.entries(enhancedData.planSteps)) {
          fullDescription += `### ${category}\n`;
          if (Array.isArray(steps)) {
            steps.forEach(step => {
              fullDescription += `- ${step}\n`;
            });
          }
          fullDescription += '\n';
        }
      }
      
      // Convert subtasks format
      const subtasks = (enhancedData.subtasks || []).map(st => 
        typeof st === 'string' 
          ? { text: st, completed: false, link: '' }
          : { text: st.task || st.text || st, completed: st.completed || false, link: st.link || '' }
      );
      
      await createIdea(user.uid, {
        idea: enhancedData.title,
        description: fullDescription,
        tags: [], // Will be resolved to IDs by IdeaForm if needed
        status: 'Drafted',
        rating: 0,
        subtasks: subtasks,
        lessonsLearned: '',
        resources: [],
        color: null,
        developmentStatus: 'developed',
        developmentPath: 'autopilot',
        lastDevelopedAt: serverTimestamp()
      });

      try {
        await consumeFreePassAutopilot(newFreePassIdempotencyKey());
      } catch (consumeErr) {
        console.error('[IdeaList] consumeFreePassAutopilot:', consumeErr);
        alert(consumeErr.message || 'Could not record complimentary pass usage. Contact Support if this persists.');
      }
      
      setPendingDescription('');
      setEnhancement(null);
      setIsEnhancementModalOpen(false);
    } catch (error) {
      console.error('Error saving enhanced idea:', error);
      alert('Failed to save idea. Please try again.');
    } finally {
      setFetchLoading(false);
    }
  };

  // Handle Guided Journey completion
  const handleGuidedComplete = async (completedData) => {
    if (!user) return;
    
    try {
      setFetchLoading(true);
      
      // Format plan steps as markdown and append to description
      let fullDescription = completedData.description || '';
      if (completedData.planSteps && Object.keys(completedData.planSteps).length > 0) {
        fullDescription += '\n\n## Plan Steps\n\n';
        for (const [category, steps] of Object.entries(completedData.planSteps)) {
          fullDescription += `### ${category}\n`;
          if (Array.isArray(steps)) {
            steps.forEach(step => {
              fullDescription += `- ${step}\n`;
            });
          }
          fullDescription += '\n';
        }
      }
      
      // Convert subtasks format
      const subtasks = (completedData.subtasks || []).map(st => 
        typeof st === 'string' 
          ? { text: st, completed: false, link: '' }
          : { text: st.task || st.text || st, completed: st.completed || false, link: st.link || '' }
      );
      
      await createIdea(user.uid, {
        idea: completedData.title,
        description: fullDescription,
        tags: [], // Tags will need to be resolved
        status: 'Drafted',
        rating: 0,
        subtasks: subtasks,
        lessonsLearned: '',
        resources: [],
        color: null,
        developmentStatus: 'developed',
        developmentPath: 'guided',
        lastDevelopedAt: serverTimestamp()
      });
      
      setPendingDescription('');
      setIsGuidedJourneyOpen(false);
    } catch (error) {
      console.error('Error saving guided idea:', error);
      alert('Failed to save idea. Please try again.');
    } finally {
      setFetchLoading(false);
    }
  };

  // Handle Guided Journey partial save (abandonment)
  const handleGuidedSavePartial = async (partialData) => {
    if (!user) return;
    
    try {
      setFetchLoading(true);
      
      // Convert subtasks format
      const subtasks = (partialData.subtasks || []).map(st => 
        typeof st === 'string' 
          ? { text: st, completed: false, link: '' }
          : { text: st.task || st.text || st, completed: st.completed || false, link: st.link || '' }
      );
      
      await createIdea(user.uid, {
        idea: partialData.title || pendingDescription.substring(0, 50),
        description: partialData.description || pendingDescription,
        tags: [],
        status: 'Drafted',
        rating: 0,
        subtasks: subtasks,
        lessonsLearned: '',
        resources: [],
        color: null,
        developmentStatus: 'raw',
        developmentPath: 'guided', // Track that it was started as guided
        lastDevelopedAt: null
      });
      
      setPendingDescription('');
    } catch (error) {
      console.error('Error saving partial idea:', error);
      alert('Failed to save idea. Please try again.');
    } finally {
      setFetchLoading(false);
    }
  };

  // State for re-development from detail view
  const [redevelopingIdea, setRedevelopingIdea] = useState(null);

  // Handle Develop button click from detail modal
  const handleDevelopFromDetail = (idea) => {
    setRedevelopingIdea(idea);
    setPendingDescription(idea.description || '');
    setIsDetailModalOpen(false);
    setIsPathSelectorOpen(true);
  };

  // Handle path selection for re-development (update existing idea instead of create)
  const handlePathSelectForRedevelopment = async (path) => {
    setIsPathSelectorOpen(false);
    
    if (path === 'raw') {
      // Raw doesn't make sense for re-development - just close
      setRedevelopingIdea(null);
      setPendingDescription('');
      return;
    }
    
    if (path === 'autopilot') {
      await handleAutopilotRedevelopment();
    } else if (path === 'guided') {
      setIsGuidedJourneyOpen(true);
    }
  };

  // Autopilot re-development (update existing idea)
  const handleAutopilotRedevelopment = async () => {
    if (!user || !redevelopingIdea) return;
    
    try {
      setIsEnhancing(true);
      const result = await enhanceIdea(pendingDescription);
      setEnhancement(result);
      setIsEnhancementModalOpen(true);
    } catch (error) {
      console.error('Error enhancing idea:', error);
      alert(error.message || 'Failed to enhance idea. Please try again.');
      setRedevelopingIdea(null);
    } finally {
      setIsEnhancing(false);
    }
  };

  // Handle applying enhancement for re-development (update instead of create)
  const handleApplyEnhancementForRedevelopment = async (enhancedData) => {
    if (!user || !redevelopingIdea) return;
    
    try {
      setFetchLoading(true);
      
      // Format plan steps as markdown and append to description
      let fullDescription = enhancedData.description || '';
      if (enhancedData.planSteps && Object.keys(enhancedData.planSteps).length > 0) {
        fullDescription += '\n\n## Plan Steps\n\n';
        for (const [category, steps] of Object.entries(enhancedData.planSteps)) {
          fullDescription += `### ${category}\n`;
          if (Array.isArray(steps)) {
            steps.forEach(step => {
              fullDescription += `- ${step}\n`;
            });
          }
          fullDescription += '\n';
        }
      }
      
      // Convert subtasks format
      const subtasks = (enhancedData.subtasks || []).map(st => 
        typeof st === 'string' 
          ? { text: st, completed: false, link: '' }
          : { text: st.task || st.text || st, completed: st.completed || false, link: st.link || '' }
      );
      
      await updateIdea(user.uid, redevelopingIdea.id, {
        ...redevelopingIdea,
        idea: enhancedData.title,
        description: fullDescription,
        subtasks: subtasks,
        developmentStatus: 'developed',
        developmentPath: 'autopilot',
        lastDevelopedAt: serverTimestamp()
      });

      try {
        await consumeFreePassAutopilot(newFreePassIdempotencyKey());
      } catch (consumeErr) {
        console.error('[IdeaList] consumeFreePassAutopilot (redevelop):', consumeErr);
        alert(consumeErr.message || 'Could not record complimentary pass usage. Contact Support if this persists.');
      }
      
      setPendingDescription('');
      setEnhancement(null);
      setIsEnhancementModalOpen(false);
      setRedevelopingIdea(null);
    } catch (error) {
      console.error('Error updating idea:', error);
      alert('Failed to update idea. Please try again.');
    } finally {
      setFetchLoading(false);
    }
  };

  // Handle Guided Journey completion for re-development
  const handleGuidedCompleteForRedevelopment = async (completedData) => {
    if (!user || !redevelopingIdea) return;
    
    try {
      setFetchLoading(true);
      
      // Format plan steps as markdown and append to description
      let fullDescription = completedData.description || '';
      if (completedData.planSteps && Object.keys(completedData.planSteps).length > 0) {
        fullDescription += '\n\n## Plan Steps\n\n';
        for (const [category, steps] of Object.entries(completedData.planSteps)) {
          fullDescription += `### ${category}\n`;
          if (Array.isArray(steps)) {
            steps.forEach(step => {
              fullDescription += `- ${step}\n`;
            });
          }
          fullDescription += '\n';
        }
      }
      
      // Convert subtasks format
      const subtasks = (completedData.subtasks || []).map(st => 
        typeof st === 'string' 
          ? { text: st, completed: false, link: '' }
          : { text: st.task || st.text || st, completed: st.completed || false, link: st.link || '' }
      );
      
      await updateIdea(user.uid, redevelopingIdea.id, {
        ...redevelopingIdea,
        idea: completedData.title,
        description: fullDescription,
        subtasks: subtasks,
        developmentStatus: 'developed',
        developmentPath: 'guided',
        lastDevelopedAt: serverTimestamp()
      });
      
      setPendingDescription('');
      setIsGuidedJourneyOpen(false);
      setRedevelopingIdea(null);
    } catch (error) {
      console.error('Error updating idea:', error);
      alert('Failed to update idea. Please try again.');
    } finally {
      setFetchLoading(false);
    }
  };

  const handleSort = (ideas) => {
    return [...ideas].sort((a, b) => {
      let comparison = 0;
      switch (sortBy) {
        case 'rating':
          comparison = (b.rating || 0) - (a.rating || 0);
          break;
        case 'status':
          comparison = (STATUS_ORDER[a.status] || 999) - (STATUS_ORDER[b.status] || 999);
          break;
        case 'tag': {
          const aTag = a.tags?.[0] ? tags[a.tags[0]] : '';
          const bTag = b.tags?.[0] ? tags[b.tags[0]] : '';
          comparison = aTag.localeCompare(bTag);
          break;
        }
        default:
          return 0;
      }
      return sortDirection === 'desc' ? comparison : -comparison;
    });
  };

  const filteredAndSortedIdeas = React.useMemo(() => {
    return handleSort(
      ideas.filter(idea => 
        (selectedTags.length === 0 || selectedTags.every(tag => idea.tags?.includes(tag))) &&
        (selectedStatuses.length === 0 || selectedStatuses.includes(idea.status))
      )
    );
  }, [ideas, selectedTags, selectedStatuses, handleSort]);


  // handleStatusChange removed - unused

  const TagFilter = (
    <div className="w-full">
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        Filter by Tags
      </label>
      <div className="flex flex-wrap gap-2 p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 min-h-[42px]">
        {selectedTags.length === 0 ? (
          <span className="text-gray-400 dark:text-gray-500 text-sm">Select tags...</span>
        ) : (
          selectedTags.map((tagId) => {
            const tag = availableTags.find(t => t.id === tagId);
            return tag ? (
              <span
                key={tagId}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-primary/20 text-primary"
              >
                {tag.name}
                <button
                  onClick={() => {
                    const newTags = selectedTags.filter(id => id !== tagId);
                    setSelectedTags(newTags);
                  }}
                  className="hover:bg-primary/30 rounded-full p-0.5"
                >
                  ×
                </button>
              </span>
            ) : null;
          })
        )}
      </div>
      <div className="mt-2 flex flex-wrap gap-2">
        {availableTags.map((tag) => {
          const isSelected = selectedTags.includes(tag.id);
          return (
            <button
              key={tag.id}
              type="button"
              onClick={() => {
                const newTags = isSelected
                  ? selectedTags.filter(id => id !== tag.id)
                  : [...selectedTags, tag.id];
                setSelectedTags(newTags);
              }}
              className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                isSelected
                  ? 'bg-primary text-white'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
              }`}
            >
              {tag.name}
            </button>
          );
        })}
      </div>
    </div>
  );

  const StatusFilter = (
    <div className="w-full">
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        Filter by Status
      </label>
      <div className="flex flex-wrap gap-2 p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 min-h-[42px]">
        {selectedStatuses.length === 0 ? (
          <span className="text-gray-400 dark:text-gray-500 text-sm">Select statuses...</span>
        ) : (
          selectedStatuses.map((status) => (
            <span
              key={status}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-primary/20 text-primary"
            >
              {status}
              <button
                onClick={() => {
                  setSelectedStatuses(selectedStatuses.filter(s => s !== status));
                }}
                className="hover:bg-primary/30 rounded-full p-0.5"
              >
                ×
              </button>
            </span>
          ))
        )}
      </div>
      <div className="mt-2 flex flex-wrap gap-2">
        {statuses.map((status) => {
          const isSelected = selectedStatuses.includes(status);
          return (
            <button
              key={status}
              type="button"
              onClick={() => {
                const newStatuses = isSelected
                  ? selectedStatuses.filter(s => s !== status)
                  : [...selectedStatuses, status];
                setSelectedStatuses(newStatuses);
              }}
              className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                isSelected
                  ? 'bg-primary text-white'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
              }`}
            >
              {status}
            </button>
          );
        })}
      </div>
    </div>
  );

  const SortBy = (
    <div className="w-full">
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        Sort By
      </label>
      <div className="flex gap-2">
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
        >
          <option value="rating">Sort by Rating</option>
          <option value="status">Sort by Status</option>
          <option value="tag">Sort by Tag</option>
        </select>
        <button
          onClick={() => setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')}
          className={`px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
            sortDirection === 'asc' ? 'bg-primary/10 border-primary' : ''
          }`}
          aria-label="Toggle sort direction"
        >
          <ArrowUpDown className="w-5 h-5" />
        </button>
      </div>
    </div>
  );

  if (loading || fetchLoading) {
    return (
      <div className="flex justify-center items-center my-8">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (authError) {
    return (
      <div className="p-4 bg-red-100 dark:bg-red-900/30 border border-red-400 dark:border-red-700 text-red-700 dark:text-red-300 rounded">
        {authError.message}
      </div>
    );
  }

  if (!user) {
    return (
      <p className="text-gray-600 dark:text-gray-400 text-center py-8">
        Please sign in to view ideas
      </p>
    );
  }

  if (ideas.length === 0) {
    return (
      <>
        {/* Quick Input for empty state too */}
        <QuickIdeaInput 
          onSubmit={handleQuickIdeaSubmit} 
          disabled={isEnhancing || fetchLoading}
        />
        {showSignupVerifyBanner && user?.email && (
          <div className="mb-4 p-4 rounded-xl border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 text-sm text-green-900 dark:text-green-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <p>
              We sent a verification link to <strong className="font-medium">{user.email}</strong>. Verify to use your complimentary AI pass (Autopilot &amp; Guided).
            </p>
            <button
              type="button"
              onClick={() => setShowSignupVerifyBanner(false)}
              className="text-green-800 dark:text-green-200 underline font-medium shrink-0"
            >
              Dismiss
            </button>
          </div>
        )}
        
        <EmptyState onCreateIdea={handleCreateIdea} />
        
        {/* Modals */}
        <PathSelectorModal
          isOpen={isPathSelectorOpen}
          onClose={() => setIsPathSelectorOpen(false)}
          onSelectPath={handlePathSelect}
          description={pendingDescription}
          canUseEnhancementProduct={enhancementEntitlement.allowed}
          entitlementLoading={entitlementLoading}
          entitlementFooterHint={pathSelectorFooterHint}
          needsEmailVerification={needsEmailVerificationForComplimentaryAi}
          onResendVerificationEmail={handleResendVerificationEmail}
          onRefreshAuthAfterVerification={handleRefreshAuthAfterVerification}
          verificationResendBusy={verificationResendBusy}
          verificationReloadBusy={verificationReloadBusy}
          verificationNotice={verificationNotice}
          showSubscribeCta={showSubscribeCta}
          showManageBillingCta={showManageBillingCta}
          onSubscribe={handleSubscribeCheckout}
          onManageBilling={handleManageBilling}
          billingActionBusy={billingActionBusy}
        />
        
        <GuidedJourneyModal
          isOpen={isGuidedJourneyOpen}
          onClose={() => setIsGuidedJourneyOpen(false)}
          originalDescription={pendingDescription}
          onComplete={handleGuidedComplete}
          onSavePartial={handleGuidedSavePartial}
          entitlementBlockedHint={
            needsEmailVerificationForComplimentaryAi
              ? 'Verify your email to use your complimentary AI pass.'
              : (!enhancementEntitlement.allowed && !entitlementLoading
                  ? pathSelectorFooterHint
                  : null)
          }
        />
        
        <IdeaEnhancementModal
          isOpen={isEnhancementModalOpen}
          onClose={() => {
            setIsEnhancementModalOpen(false);
            setEnhancement(null);
          }}
          enhancement={enhancement}
          onApply={handleApplyEnhancement}
          onRegenerate={handleRegenerate}
          isRegenerating={isRegenerating}
        />
        
        {/* Loading overlay for Autopilot */}
        {isEnhancing && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center">
            <div className="bg-white dark:bg-gray-800 rounded-xl p-8 flex flex-col items-center">
              <Loader2 className="w-12 h-12 animate-spin text-primary mb-4" />
              <p className="text-gray-900 dark:text-white font-medium">Developing your idea...</p>
              <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">This may take a moment</p>
            </div>
          </div>
        )}
      </>
    );
  }

  // Get active filter labels for display
  const activeTagLabels = selectedTags.map(tagId => {
    const tag = availableTags.find(t => t.id === tagId);
    return tag ? { id: tagId, name: tag.name } : null;
  }).filter(Boolean);

  return (
    <div>
      {/* Quick Idea Input */}
      <QuickIdeaInput 
        onSubmit={handleQuickIdeaSubmit} 
        disabled={isEnhancing || fetchLoading}
      />
      {showSignupVerifyBanner && user?.email && (
        <div className="mb-4 p-4 rounded-xl border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 text-sm text-green-900 dark:text-green-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <p>
            We sent a verification link to <strong className="font-medium">{user.email}</strong>. Verify to use your complimentary AI pass (Autopilot &amp; Guided).
          </p>
          <button
            type="button"
            onClick={() => setShowSignupVerifyBanner(false)}
            className="text-green-800 dark:text-green-200 underline font-medium shrink-0"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Mobile: Active Filters Bar */}
      <div className="md:hidden mb-2">
        {(selectedTags.length > 0 || selectedStatuses.length > 0) ? (
          <div className="flex items-center gap-2 overflow-x-auto pb-2">
            {activeTagLabels.map(tag => (
              <span
                key={tag.id}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-primary/20 text-primary whitespace-nowrap"
              >
                {tag.name}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedTags(selectedTags.filter(id => id !== tag.id));
                  }}
                  className="hover:bg-primary/30 rounded-full p-0.5"
                >
                  ×
                </button>
              </span>
            ))}
            {selectedStatuses.map(status => (
              <span
                key={status}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-primary/20 text-primary whitespace-nowrap"
              >
                {status}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedStatuses(selectedStatuses.filter(s => s !== status));
                  }}
                  className="hover:bg-primary/30 rounded-full p-0.5"
                >
                  ×
                </button>
              </span>
            ))}
            <button
              onClick={() => setIsFiltersOpen(!isFiltersOpen)}
              className="flex items-center gap-1 px-3 py-1 bg-gray-100 dark:bg-gray-800 rounded-full text-xs font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors whitespace-nowrap ml-auto"
            >
              <SlidersHorizontal className="w-3 h-3" />
              Filter / Sort
            </button>
          </div>
        ) : (
          <button
            onClick={() => setIsFiltersOpen(!isFiltersOpen)}
            className="flex items-center gap-2 px-3 py-2 w-full bg-gray-100 dark:bg-gray-800 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
          >
            <SlidersHorizontal className="w-4 h-4" />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Filter / Sort</span>
            <ChevronDown className={`w-3 h-3 transition-transform ml-auto ${isFiltersOpen ? 'rotate-180' : ''}`} />
          </button>
        )}
      </div>

      {/* Desktop: Filter Toggle Button */}
      <div className="hidden md:block mb-2">
        <button
          onClick={() => setIsFiltersOpen(!isFiltersOpen)}
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-800 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
        >
          <SlidersHorizontal className="w-5 h-5" />
          <span className="font-medium text-gray-700 dark:text-gray-300">Filter / Sort</span>
          <ChevronDown className={`w-4 h-4 transition-transform ml-auto ${isFiltersOpen ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {/* Collapsible Filter Drawer */}
      {isFiltersOpen && (
        <div className="mb-4 p-3 md:p-4 bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-gray-200 dark:border-gray-700 space-y-3 md:space-y-4 animate-in fade-in slide-in-from-top-2 duration-200">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
            <div>{TagFilter}</div>
            <div>{StatusFilter}</div>
            <div>{SortBy}</div>
          </div>
        </div>
      )}

      {/* Grid Layout */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-20">
        {filteredAndSortedIdeas.map((idea) => (
          <IdeaCard
            key={idea.id}
            idea={idea}
            onClick={() => handleCardClick(idea.id)}
          />
        ))}
      </div>

      {/* Floating Action Button */}
      <button
        onClick={handleCreateModalOpen}
        className="fixed bottom-6 right-6 w-16 h-16 bg-primary hover:bg-primary/90 text-white rounded-full shadow-xl hover:shadow-2xl hover:scale-110 transition-all z-50 flex items-center justify-center"
        aria-label="Create new idea"
      >
        <Plus className="w-8 h-8" />
      </button>

      {/* Detail Modal */}
      {selectedIdeaId && (
        <IdeaDetailModal
          idea={ideas.find(i => i.id === selectedIdeaId)}
          isOpen={isDetailModalOpen}
          onClose={handleModalClose}
          onSave={handleSaveInModal}
          onDelete={handleDeleteInModal}
          onDevelop={handleDevelopFromDetail}
          tags={tags}
          user={user}
          setHasUnsavedChanges={setHasUnsavedChanges}
        />
      )}

      {/* Create Modal */}
      <IdeaCreateModal
        isOpen={isCreateModalOpen}
        onClose={handleCreateModalClose}
        onSubmit={handleCreateIdea}
        setHasUnsavedChanges={setHasUnsavedChanges}
      />

      {/* Path Selector Modal */}
      <PathSelectorModal
        isOpen={isPathSelectorOpen}
        onClose={() => {
          setIsPathSelectorOpen(false);
          setRedevelopingIdea(null);
          setPendingDescription('');
        }}
        onSelectPath={redevelopingIdea ? handlePathSelectForRedevelopment : handlePathSelect}
        description={pendingDescription}
        canUseEnhancementProduct={enhancementEntitlement.allowed}
        entitlementLoading={entitlementLoading}
        entitlementFooterHint={pathSelectorFooterHint}
        hideRawSave={!!redevelopingIdea} // Hide Raw Save for existing ideas
        needsEmailVerification={needsEmailVerificationForComplimentaryAi}
        onResendVerificationEmail={handleResendVerificationEmail}
        onRefreshAuthAfterVerification={handleRefreshAuthAfterVerification}
        verificationResendBusy={verificationResendBusy}
        verificationReloadBusy={verificationReloadBusy}
        verificationNotice={verificationNotice}
        showSubscribeCta={showSubscribeCta}
        showManageBillingCta={showManageBillingCta}
        onSubscribe={handleSubscribeCheckout}
        onManageBilling={handleManageBilling}
        billingActionBusy={billingActionBusy}
      />
      
      {/* Guided Journey Modal */}
      <GuidedJourneyModal
        isOpen={isGuidedJourneyOpen}
        onClose={() => {
          setIsGuidedJourneyOpen(false);
          setRedevelopingIdea(null);
          setPendingDescription('');
        }}
        originalDescription={pendingDescription}
        existingTitle={redevelopingIdea?.idea || ''}
        onComplete={redevelopingIdea ? handleGuidedCompleteForRedevelopment : handleGuidedComplete}
        onSavePartial={redevelopingIdea ? () => {
          // For re-development, just close without saving partial
          setIsGuidedJourneyOpen(false);
          setRedevelopingIdea(null);
          setPendingDescription('');
        } : handleGuidedSavePartial}
        entitlementBlockedHint={
          needsEmailVerificationForComplimentaryAi
            ? 'Verify your email to use your complimentary AI pass.'
            : (!enhancementEntitlement.allowed && !entitlementLoading
                ? pathSelectorFooterHint
                : null)
        }
      />
      
      {/* Enhancement Modal (for Autopilot) */}
      <IdeaEnhancementModal
        isOpen={isEnhancementModalOpen}
        onClose={() => {
          setIsEnhancementModalOpen(false);
          setEnhancement(null);
          setRedevelopingIdea(null);
          setPendingDescription('');
        }}
        enhancement={enhancement}
        onApply={redevelopingIdea ? handleApplyEnhancementForRedevelopment : handleApplyEnhancement}
        onRegenerate={handleRegenerate}
        isRegenerating={isRegenerating}
      />
      
      {/* Loading overlay for Autopilot */}
      {isEnhancing && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-8 flex flex-col items-center">
            <Loader2 className="w-12 h-12 animate-spin text-primary mb-4" />
            <p className="text-gray-900 dark:text-white font-medium">Developing your idea...</p>
            <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">This may take a moment</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default IdeaList;